<template>
  <div class="bar-chart-container pt-5 pr-5">
    <BarChartGenerator
      :chart-options="chartOptions"
      :chart-data="chartData"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="plugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />
    
    <!-- Custom Legend with Rounded Rectangles -->
    <div class="custom-legend">
      <div v-for="(dataset, index) in chartData.datasets" :key="index" class="legend-item">
        <div 
          class="legend-box"
          :style="{ backgroundColor: dataset.backgroundColor }"
        ></div>
        <span class="legend-label">{{ dataset.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import {Bar as BarChartGenerator} from 'vue-chartjs/legacy';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
);

export default {
  name: 'BarChart',
  components: {
    BarChartGenerator,
  },
  props: {
    chartId: {
      type: String,
      default: 'bar-chart',
    },
    datasetIdKey: {
      type: String,
      default: 'label',
    },
    width: {
      type: Number,
      default: undefined,
    },
    height: {
      type: Number,
      default: 300,
    },
    cssClasses: {
      default: '',
      type: String,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    plugins: {
      type: Array,
      default: () => [],
    },
    systemScore: {
      type: Array,
      required: true,
    },
    interviewerScore: {
      type: Array,
      required: true,
    },
  },
  computed: {
    chartData() {
      return {
        labels: ['Knowledge', 'Fluency', 'Confidence', 'Behavioral'],
        datasets: [
          {
            label: 'System Score',
            backgroundColor: '#439AFF',
            data: this.systemScore,
            actualData: this.systemScore,
          },
          {
            label: 'Interviewer Score',
            backgroundColor: '#AC9EE4',
            data: this.interviewerScore,
            actualData: this.interviewerScore,
          },
        ],
      };
    },
  },
  mounted() {
    this.updateFontSizeForBreakpoint();
    window.addEventListener('resize', this.updateFontSizeForBreakpoint);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateFontSizeForBreakpoint);
  },
  methods: {
    updateFontSizeForBreakpoint() {
      const width = window.innerWidth;
      const isSmall = width <= 1380;
      this.chartOptions.plugins.datalabels.font.size = isSmall ? 10 : 12;
    },
  },
  data() {
    return {
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false, // Hide default legend
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const dataset = context.dataset;
                const actualValue =
                  dataset.actualData?.[context.dataIndex] || 0;
                
                return `${dataset.label}: ${actualValue}`;
              },
            },
          },
          datalabels: {
            anchor: 'end',
            align: 'start',
            offset: -20,
            formatter: (value) => {
              return `${value}`;
            },
            font: {
              size: 12,
            },
            color: '#000',
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
          },
          y: {
            beginAtZero: true,
            min: 0,
            ticks: {
              callback: function (value) {
                return value;
              },
            },
            grid: {
              display: false,
            },
            grace: '10%',
          },
        },
      },
    };
  },
};
</script>

<style scoped>
.bar-chart-container {
  width: 100%;
  height: 100%;
  max-width: 100%;
  margin: auto;
  position: relative;
  display: flex;
  flex-direction: column;
}

.custom-legend {
  display: flex;
  justify-content: center;
  gap: 24px;
  padding: 16px 0;
  flex-wrap: wrap;
  margin-top: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.legend-box {
  width: 20px;
  height: 14px;
  border-radius: 3px; /* Adjust for more/less curve: 2px (sharp), 4px (medium), 6px (rounded) */
  flex-shrink: 0;
}

.legend-label {
  font-size: 12px;
  font-weight: 500;
  font-family: "GraphikMedium", sans-serif !important;
  color: #333;
}
</style>